/**
 * The Approval panel — Resource Plan Epic 17 (RP-17.1 … RP-17.3), M4.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * ONE PANEL, TWO AUDIENCES, AND THE DIFFERENCE IS COST
 * ──────────────────────────────────────────────────────────────────────────────
 * RP-17.3 gives the PLANNER "pending / approved / rejected with actor, timestamp, and
 * reason" — which is the history table below, and rides on `viewResourcePlan`.
 *
 * RP-17.1 gives the APPROVER "margin %, floor, shortfall in dollars, the lines below cost,
 * and the planner's note", plus RP-17.2's Planned Pod and suggested Intensity. Every one of
 * those is cost-derived, so the exhibit is fetched from a separate endpoint gated on
 * `viewPlanCost` and is not requested at all when `plan.canViewCost` is false. An `[ACCT]`
 * planner therefore sees a panel that is complete for them rather than an error they can do
 * nothing about.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * 🔥 THE SUGGESTED INTENSITY IS A REFERENCE STRING
 * ──────────────────────────────────────────────────────────────────────────────
 * §4.5 / non-negotiable 1: "treat any derived points value as a display string, never a
 * persisted number". The API sends `suggestedIntensity` as a string for that reason, and
 * this panel renders it and nothing more — no arithmetic, no total, no "apply". The `[RM]`
 * confirms an Intensity at Handover in M5; this is what an approver reads beforehand.
 */

import { CheckCircle2, Clock, MinusCircle, ShieldAlert, XCircle } from "lucide-react";
import { useState } from "react";
import { InternalRateChip } from "@/components/plans/InternalRateChip";
import { describeRateSource } from "@/components/plans/rate-source";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useDecidePlanApproval,
  usePlanApproval,
  usePlanApprovals,
} from "@/hooks/use-plan-governance";
import { formatPlanHours, formatPlanMoney } from "@/lib/plan-format";
import { cn } from "@/lib/utils";
import type {
  PlanApprovalDetailView,
  PlanApprovalSummaryView,
  ResourcePlanDetailView,
} from "@/schemas/resource-plans";

const STATUS_TONE: Record<string, string> = {
  Pending: "bg-warning-100 text-warning-700 dark:text-warning-400",
  Approved: "bg-success-100 text-success-700 dark:text-success-400",
  Rejected: "bg-error-100 text-error-700 dark:text-error-400",
  // Closed without a decision — the plan moved on or the figures changed.
  Withdrawn: "bg-muted text-muted-foreground",
};

const STATUS_ICON: Record<string, typeof Clock> = {
  Pending: Clock,
  Approved: CheckCircle2,
  Rejected: XCircle,
  Withdrawn: MinusCircle,
};

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function PlanApprovalPanel({ plan }: { plan: ResourcePlanDetailView }) {
  const { data: approvals, isLoading, isError, error, refetch } = usePlanApprovals(plan.id);

  // The exhibit is only fetched for a reader who may see cost — see the header.
  const pendingId = plan.pendingApprovalId ?? null;
  const exhibitId = pendingId ?? approvals?.[0]?.id ?? null;
  const exhibit = usePlanApproval(exhibitId, plan.canViewCost);

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Could not load the approval history."}
        onRetry={refetch}
      />
    );
  }

  const rows = approvals ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-sm font-semibold">Approval</h2>
        <p className="mt-0.5 max-w-2xl text-sm leading-relaxed text-muted-foreground">
          A plan below the {plan.marginFloorPercent}% margin floor, or with a line below cost, needs
          approval from Finance or an Admin to be Proposed. A change to a plan that has already been
          sent asks again when it takes the plan below the floor.
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center">
          <ShieldAlert className="mx-auto h-5 w-5 text-muted-foreground" />
          <p className="mt-2 text-sm font-medium">No approval has been needed</p>
        </div>
      ) : (
        <PlanApprovalHistory rows={rows} />
      )}

      {plan.canViewCost && exhibitId !== null && (
        <>
          {exhibit.isLoading && <Skeleton className="h-64 w-full" />}
          {exhibit.data !== undefined && <ApprovalExhibit approval={exhibit.data} />}
        </>
      )}
    </div>
  );
}

/** RP-17.3: status, actor, timestamp and reason. Safe for the planner. */
function PlanApprovalHistory({ rows }: { rows: readonly PlanApprovalSummaryView[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-[720px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Version</th>
            <th className="px-3 py-2 font-medium">Requested</th>
            <th className="px-3 py-2 font-medium">By</th>
            <th className="px-3 py-2 font-medium">Decided</th>
            <th className="px-3 py-2 font-medium">Reason</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const Icon = STATUS_ICON[row.status] ?? Clock;
            return (
              <tr key={row.id} className="border-b border-border last:border-b-0">
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wider",
                      STATUS_TONE[row.status] ?? "bg-muted text-muted-foreground",
                    )}
                  >
                    <Icon className="h-3 w-3" />
                    {row.status}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs">v{row.versionNumber}</td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                  {when(row.requestedAt)}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{row.requestedByName ?? "—"}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {row.decidedAt === null ? (
                    <span className="italic">awaiting a decision</span>
                  ) : (
                    <span className="font-mono text-xs">
                      {when(row.decidedAt)} · {row.decidedByName ?? "—"}
                    </span>
                  )}
                </td>
                {/* RP-17.2: the rejection reason is what makes a rejection actionable, and
                    this is where the planner reads it. */}
                <td className="max-w-[280px] px-3 py-2 text-muted-foreground">
                  {row.decisionReason ?? "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * RP-17.1's exhibit and RP-17.2's decision.
 *
 * Reached only when `plan.canViewCost` — ADMIN / RM / FIN. The Approve and Reject buttons
 * appear only when the server says `canDecide`, which is `approveResourcePlan` on a Pending
 * request: `[FIN]` and `[ADMIN]` hold it, so an `[RM]` reading this sees the whole case and
 * no buttons.
 */
function ApprovalExhibit({ approval }: { approval: PlanApprovalDetailView }) {
  const decide = useDecidePlanApproval(approval.id);
  const [reason, setReason] = useState("");

  return (
    <div className="space-y-4 rounded-lg border border-border bg-muted/20 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-display text-sm font-semibold">
          {approval.origin === "Edit"
            ? "Why approval was asked for — a change after the plan was sent"
            : "Why approval was asked for"}
          <span className="ml-2 font-mono text-xs font-normal text-muted-foreground">
            v{approval.versionNumber} · {approval.rateBasisLabel}
          </span>
        </h3>
        <span className="text-xs text-muted-foreground">
          Visible to Finance, the RM and Admins. Not to the Account Owner.
        </span>
      </div>

      <div className="grid gap-2 sm:grid-cols-4">
        <Figure
          label="Margin of record"
          value={
            approval.marginOfRecordPercent === null ? "—" : `${approval.marginOfRecordPercent}%`
          }
          tone={approval.triggerBelowFloor ? "bad" : "neutral"}
        />
        <Figure label="Floor" value={`${approval.marginFloorPercent}%`} />
        <Figure
          label="Shortfall"
          value={formatPlanMoney(approval.shortfallAmount)}
          tone={approval.shortfallAmount === null ? "neutral" : "bad"}
        />
        <Figure
          label="Fee Variance"
          value={
            approval.feeVariance === null ? "n/a — T&M" : formatPlanMoney(approval.feeVariance)
          }
        />
      </div>

      {approval.requestedNote !== null && (
        <div className="rounded-md border border-border bg-background px-3 py-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            The planner&rsquo;s note
          </p>
          <p className="mt-1 text-sm leading-relaxed">{approval.requestedNote}</p>
        </div>
      )}

      {approval.belowCostLines.length > 0 && (
        <div>
          <p className="mb-1.5 text-sm font-medium">
            {approval.belowCostLines.length} line
            {approval.belowCostLines.length === 1 ? "" : "s"} priced below cost
          </p>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-2.5 py-1.5 font-medium">Labor Category</th>
                  <th className="px-2.5 py-1.5 text-right font-medium">Applied</th>
                  <th className="px-2.5 py-1.5 font-medium">Rate source</th>
                  <th className="px-2.5 py-1.5 text-right font-medium">Cost</th>
                  <th className="px-2.5 py-1.5 text-right font-medium">Gap / hr</th>
                  <th className="px-2.5 py-1.5 text-right font-medium">Hours</th>
                </tr>
              </thead>
              <tbody>
                {approval.belowCostLines.map((line) => (
                  <tr key={line.planLineId} className="border-b border-border last:border-b-0">
                    <td className="px-2.5 py-1.5">{line.laborCategoryName}</td>
                    <td className="px-2.5 py-1.5 text-right font-mono">
                      {formatPlanMoney(line.billRate)}
                    </td>
                    <td className="px-2.5 py-1.5 text-xs text-muted-foreground">
                      {line.rateSource === undefined
                        ? "—"
                        : describeRateSource(
                            { rateSource: line.rateSource, listRate: line.listRate ?? null },
                            approval,
                          ).detail}
                    </td>
                    {line.costRate === null || line.deltaPerHour === null ? (
                      // A named Resource's Internal Rate — withheld from this reader.
                      <td colSpan={2} className="px-2.5 py-1.5 text-right">
                        <InternalRateChip />
                      </td>
                    ) : (
                      <>
                        <td className="px-2.5 py-1.5 text-right font-mono">
                          {formatPlanMoney(line.costRate)}
                        </td>
                        <td className="px-2.5 py-1.5 text-right font-mono text-error-700 dark:text-error-400">
                          −{formatPlanMoney(line.deltaPerHour)}
                        </td>
                      </>
                    )}
                    <td className="px-2.5 py-1.5 text-right font-mono">
                      {formatPlanHours(line.totalHours)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* RP-17.2: "The approval screen shows the Planned Pod, Shape Source … and suggested
          Intensity per month, so the approver sees what staffing is about to inherit." */}
      <div className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-md border border-border bg-background px-3 py-2.5">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Planned Pod</p>
          {approval.plannedPod === null ? (
            <p className="mt-1 text-sm italic text-muted-foreground">
              No Planned Pod composed. Handover will have no proposed Pod to staff from.
            </p>
          ) : (
            <>
              <p className="mt-1 text-sm font-medium">{approval.plannedPod.originSummary}</p>
              <ul className="mt-1.5 space-y-0.5">
                {approval.plannedPod.positions.map((position) => (
                  <li key={position.id} className="flex items-baseline gap-2 text-sm">
                    <span className="w-7 font-mono font-semibold">{position.positionCode}</span>
                    <span className="text-muted-foreground">
                      {position.expectedDisciplineCode}
                      {position.laborCategoryName === null
                        ? " · no priced Labor Category"
                        : ` · ${position.laborCategoryName}`}
                    </span>
                    <span className="ml-auto font-mono text-muted-foreground">
                      {formatPlanHours(position.totalHours)} h
                      {position.hasPlannedWork ? "" : " · no planned work"}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="rounded-md border border-border bg-background px-3 py-2.5">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Suggested Intensity per month
          </p>
          {approval.suggestedIntensityByMonth.length === 0 ? (
            <p className="mt-1 text-sm italic text-muted-foreground">No hours planned yet.</p>
          ) : (
            <>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {approval.suggestedIntensityByMonth.map((month) => (
                  <span
                    key={month.month}
                    title={`${formatPlanHours(month.hours)} planned hours · ${month.podComposition}`}
                    className="inline-flex items-baseline gap-1 rounded border border-border px-1.5 py-0.5 font-mono text-xs"
                  >
                    <span className="text-muted-foreground">{month.month.slice(2)}</span>
                    <span className="font-semibold">I{month.suggestedIntensity}</span>
                  </span>
                ))}
              </div>
              {/* Non-negotiable 1: a suggestion, never a write. */}
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                A reference figure — nothing here writes an Intensity.
              </p>
            </>
          )}
        </div>
      </div>

      {approval.canDecide ? (
        <div className="space-y-2 border-t border-border pt-3">
          <div className="space-y-1">
            <Label htmlFor="decision-reason" className="text-sm">
              Reason
              <span className="ml-1 font-normal text-muted-foreground">
                — required to reject, and visible to the planner
              </span>
            </Label>
            <Input
              id="decision-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Take the SA rate back to $230 and re-submit"
              className="h-8 text-sm"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={decide.isPending}
              onClick={() =>
                decide.mutate({
                  decision: "Approved",
                  reason: reason.trim() === "" ? null : reason.trim(),
                })
              }
            >
              <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
              {approval.origin === "Edit" ? "Approve" : "Approve and propose"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-destructive"
              disabled={decide.isPending || reason.trim() === ""}
              title={
                reason.trim() === "" ? "A rejection needs a reason the planner can act on." : ""
              }
              onClick={() => decide.mutate({ decision: "Rejected", reason: reason.trim() })}
            >
              <XCircle className="mr-1.5 h-3.5 w-3.5" />
              Reject
            </Button>
            <span className="text-xs text-muted-foreground">
              {approval.origin === "Edit"
                ? "Approving records the decision. The plan keeps its status and the change stays."
                : "Approving freezes an Approved version and moves the plan to Proposed."}
            </span>
          </div>
        </div>
      ) : (
        approval.status === "Pending" && (
          <p className="border-t border-border pt-3 text-sm text-muted-foreground">
            Waiting on Finance or an Admin to decide.
          </p>
        )
      )}
    </div>
  );
}

function Figure({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "bad";
}) {
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={cn(
          "mt-0.5 font-mono text-sm font-semibold",
          tone === "bad" && "text-error-700 dark:text-error-400",
        )}
      >
        {value}
      </p>
    </div>
  );
}
